import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Paper, Button } from "@mui/material";
import { get } from "lodash";
import { Switch, FormControlLabel } from "@mui/material";
import { styled } from "styled-components";
import { styled as muiStyled } from "@mui/material/styles";

const getOptions = (
  allowMarketing,
  setMarketing,
  allowPersonalization,
  setPersonalization,
  allowAnalytics,
  setAnalytics
) => [
  {
    label: "Personalization",
    allow: allowPersonalization,
    setChoice: setPersonalization,
    detail: `Enable a website to remember information that changes the way the website behaves or looks, like your preferred language or the region that you are in.`,
  },
  {
    label: "Marketing",
    allow: allowMarketing,
    setChoice: setMarketing,
    detail: `Used to track visitors across websites. The intention is to display ads that are relevant and engaging for the individual user and thereby more valuable for publishers and third party advertisers.`,
  },
  {
    label: "Analytics",
    allow: allowAnalytics,
    setChoice: setAnalytics,
    detail: `Help website owners to understand how visitors interact with websites by collecting and reporting information anonymously.`,
  },
];

const StlFormControlLabel = styled(FormControlLabel)`
  &.custom-label {
    position: relative;
    .MuiFormControlLabel-label {
      position: absolute;
      left: 8px;
      font-size: 12px;
      color: white;
      margin-top: 2px;
    }
    &.label-disabled {
      .MuiFormControlLabel-label {
        color: #979797;
        right: 8px;
        left: auto;
      }
    }
    .MuiSwitch-track {
      background: #e0e0e0;
      opacity: 1;
    }
  }
`;

const StlWrapper = styled.div`
  color: #5b5d62;
  a,
  .disclaimer {
    color: #5b5d62;
  }
  .closeIcon {
    font-size: 20px;
  }
  .title {
    margin-bottom: 20px;
  }
`;

const MuiRoot = muiStyled("div")(({ theme }) => ({
  position: "fixed",
  top: "0",
  left: "0",
  right: "0",
  bottom: "0",
  zIndex: "10000000",
  background: `#00000055`,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",

  "& h2": {
    fontSize: "20px",
  },
  "& h3": {
    fontSize: "16px",
  },
  "& p": {
    fontSize: "12px",
    lineHeight: "16px",
  },
  "& a": {
    textDecoration: "underline",
  },
  "& > div": {
    maxHeight: "90%",
    overflow: "auto",
    position: "relative",
    maxWidth: "516px",
    display: "flex",
    flexDirection: "column",
    margin: "10px",
    padding: "50px",
    background: "white",
    [theme.breakpoints.down("sm")]: {
      padding: "30px",
    },
    "& label": {
      marginLeft: "0",
    },
    "& > *": {
      marginBottom: "20px",
    },
    "& > *:last-child": {
      marginBottom: "0",
    },
    "& h3": {
      marginBottom: "12px",
    },
    ".closeIcon": {
      position: "absolute",
      top: 15,
      right: 15,
      cursor: "pointer",
    },
    ".button": {
      margin: "30px auto 0",
      width: "100%",
      display: "block",
    },
  },
}));

const getMarketingDefault = (complianceSettings) =>
  complianceSettings.needsToAccept ? true : complianceSettings.marketing;
const getAnalyticsDefault = (complianceSettings) =>
  complianceSettings.needsToAccept ? true : complianceSettings.analytics;
const getPersonalizationDefault = (complianceSettings) =>
  complianceSettings.needsToAccept ? true : complianceSettings.personalization;

export default function CookieComplianceDialog() {
  const showDialog = useSelector((state) =>
    get(state, "cookieCompliance.show")
  );

  if (showDialog === "detail") {
    return <ComplianceDetails />;
  } else return null;
}

function ComplianceDetails() {
  const dispatch = useDispatch();
  const showDialog = useSelector((state) =>
    get(state, "cookieCompliance.show")
  );
  const settings = useSelector((state) => get(state, "cookieCompliance"));
  const [marketing, setMarketing] = useState(getMarketingDefault(settings));
  const [personalization, setPersonalization] = useState(
    getPersonalizationDefault(settings)
  );
  const [analytics, setAnalytics] = useState(getAnalyticsDefault(settings));

  const ccpaChoices = getOptions(
    marketing,
    setMarketing,
    personalization,
    setPersonalization,
    analytics,
    setAnalytics
  );

  const saveSettings = () => {
    dispatch({
      type: "COOKIE_COMPLIANCE/SAVE",
      marketing,
      personalization,
      analytics,
    });
    window.location.reload();
  };

  const closeDetails = () => {
    dispatch({
      type: "COOKIE_COMPLIANCE/HIDE",
    });
  };

  if (!showDialog) {
    return null;
  }
  return (
    <MuiRoot>
      <Paper>
        <StlWrapper>
          <h2 className="title">Cookie Settings</h2>
          {ccpaChoices.map((choice, key) => (
            <ComplianceChoice
              className="mb-30"
              key={key}
              label={choice.label}
              detail={choice.detail}
              allow={choice.allow}
              setChoice={choice.setChoice}
            />
          ))}
          <p className="disclaimer text-info">
            For more info, view the{" "}
            <a
              className="underline"
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.sunnyside.shop/privacy-policy"
            >
              Privacy Policy
            </a>
            .
          </p>
          <Button
            className="button"
            color="secondary"
            size="large"
            variant="contained"
            onClick={saveSettings}
          >
            Save Settings
          </Button>
          <div className="closeIcon" onClick={closeDetails}>
            X
          </div>
        </StlWrapper>
      </Paper>
    </MuiRoot>
  );
}

function ComplianceChoice({ label, setChoice, allow, detail, className }) {
  return (
    <div className={className}>
      <div
        style={{ display: "flex", justifyContent: "space-between" }}
        className="flex-row-spaced mb-12"
      >
        <h3>{label}</h3>
        <StlFormControlLabel
          label={allow ? "On" : "Off"}
          className={
            allow ? "custom-label label-enabled" : "custom-label label-disabled"
          }
          labelPlacement="start"
          control={
            <Switch
              color="primary"
              checked={allow}
              onChange={() => setChoice(!allow)}
            />
          }
        />
      </div>
      <p style={{ fontSize: "12px", lineHeight: "1.2" }}>{detail}</p>
    </div>
  );
}
